import React, { useState, useEffect } from 'react';
import { Button, Alert, Form, ListGroup, HistoryPanel, StatusBadge } from '@/components/ui';
import { Task } from '../types/Task';
import { taskService } from '../services/quantifiedTaskService';
import { routineTaskService } from '../services/routineTaskService';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import ResponsiveModal from './ResponsiveModal';
import { getTaskHistoryAttribution } from '../utils/taskHistoryAttribution';

interface TaskHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  task: Task;
  onAddComment: (updatedTask: Task) => void;
}

const TaskHistoryModal: React.FC<TaskHistoryModalProps> = ({
  visible,
  onClose,
  task,
  onAddComment
}) => {
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localHistory, setLocalHistory] = useState(task.taskHistory || []);

  // Update local history when task changes
  useEffect(() => {
    setLocalHistory(task.taskHistory || []);
  }, [task.taskHistory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser?.id) return;
    
    // Check permission based on task type
    const isRoutineTask = task.type === 'Routine' || (task.type as string).toLowerCase() === 'routine';
    const canAddComment = isRoutineTask 
      ? true // For routine tasks, we'll allow if user can edit (you may want to add specific permission check)
      : hasPermission('quantified:history_create');
    
    if (!canAddComment) return;
    
    try {
      setIsSubmitting(true);
      console.log('Submitting comment for task:', task.id);
      
      // Use appropriate service based on task type
      let taskForParent: Task;
      
      if (isRoutineTask) {
        const updatedRoutineTask = await routineTaskService.addHistoryComment(task.id, {
          comments: newComment.trim(),
          userId: currentUser.id,
          isExternal: false
        });
        
        // Convert RoutineTask to Task format
        taskForParent = {
          ...task,
          contractYear: task.contractYear ?? '',
          identifiedAccomplishment: task.identifiedAccomplishment ?? 0,
          completionAccomplishment: task.completionAccomplishment ?? 0,
          projectId: task.projectId ?? 0,
          type: task.type,
          status: task.status,
          mode: task.mode ?? 'specific',
          description: task.description ?? '',
          instructions: task.instructions ?? '',
          taskHistory: updatedRoutineTask.taskHistory || []
        } as Task;
      } else {
        const updatedTask = await taskService.addHistoryComment(task.id, {
          comments: newComment.trim(),
          userId: currentUser.id,
          isExternal: false
        });
        taskForParent = updatedTask;
      }

      // Update local history with the new comment
      setLocalHistory(taskForParent.taskHistory || []);
      setNewComment('');
      
      // Notify parent with the updated task
      onAddComment(taskForParent);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedHistory = localHistory.sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  // Helper function to parse and format history comments, especially for effort_applied
  const formatHistoryComments = (history: any) => {
    if (!history.comments) return null;
    
    // For effort_applied entries, parse the form information
    if (history.stage === 'effort_applied') {
      const lines = history.comments.split('\n');
      const formInfo = lines.find((line: string) => line.includes('from submission') || line.includes('submission'));
      const otherInfo = lines.filter((line: string) => !line.includes('from submission') && !line.includes('submission'));
      
      return (
        <div className="tw-mb-1">
          {formInfo && (
            <div className="tw-mb-2">
              <strong className="tw-text-primary">
                <i className="bi bi-file-earmark-text tw-mr-1"></i>
                {formInfo}
              </strong>
            </div>
          )}
          {otherInfo.length > 0 && (
            <div className="tw-ml-3">
              {otherInfo.map((line: string, idx: number) => (
                <div key={idx} className="tw-text-sm tw-text-content-muted">{line}</div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // For other entries, display comments as-is but with better formatting
    const lines = history.comments.split('\n');
    if (lines.length > 1) {
      return (
        <div className="tw-mb-1">
          {lines.map((line: string, idx: number) => (
            <div key={idx} className={idx === 0 ? 'tw-font-semibold' : ''}>{line}</div>
          ))}
        </div>
      );
    }

    return <p className="tw-mb-1">{history.comments}</p>;
  };

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title="Task History"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <HistoryPanel
        composer={
          (task.type === 'Routine' || (task.type as string).toLowerCase() === 'routine' || hasPermission('quantified:history_create')) ? (
            <form onSubmit={handleSubmit}>
              <div className="tw-mb-3">
                <Form.Label>Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  // Shorter on a phone, where the composer sits above the timeline and a
                  // six-row textarea would push the history off screen on its own.
                  rows={3}
                  className="md:tw-h-auto"
                  placeholder="Enter your comment here..."
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                className="tw-w-full"
                disabled={!newComment.trim() || isSubmitting}
              >
                {isSubmitting ? 'Adding...' : 'Add Comment'}
              </Button>
            </form>
          ) : (
            <Alert variant="warning">
              <i className="bi bi-exclamation-triangle tw-mr-2"></i>
              You don't have permission to add comments to task history.
            </Alert>
          )
        }
      >
        {sortedHistory && sortedHistory.length > 0 ? (
          <ListGroup>
            {sortedHistory.map((history) => (
              <ListGroup.Item key={history.id}>
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-min-w-0 tw-grow">
                    <h6 className="tw-mb-1">
                      {history.summary}
                    </h6>
                    <p className="tw-mb-1 tw-text-content-muted">
                      Stage: <StatusBadge status={history.stage} />
                    </p>
                    {formatHistoryComments(history)}
                    <small className="tw-text-content-muted">
                      {getTaskHistoryAttribution(history, task)}
                    </small>
                  </div>
                  <small className="tw-shrink-0 tw-text-content-muted tw-ml-2">
                    {new Date(history.createdAt).toLocaleString()}
                  </small>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="info">No history records found.</Alert>
        )}
      </HistoryPanel>
    </ResponsiveModal>
  );
};

export default TaskHistoryModal; 